/**
 * @fileoverview Utility for filtering and sorting visible menu items.
 */

import { AppLinkTypes, AppLinkKey } from '@app/config/appLink'
import type { FeatureFlagKey } from '@app/types'

export interface AppLinkWithChildren extends AppLinkTypes {
  children?: AppLinkTypes[]
}

function isItemVisible(
  item: AppLinkTypes,
  isLoggedIn: boolean,
  featureFlagMap: Record<FeatureFlagKey, boolean>,
): boolean {
  const requiresAuth = item.requiresAuth ?? true
  const isAuthOk = isLoggedIn || requiresAuth === false

  const featureKey = item.featureFlag as FeatureFlagKey | undefined
  const featureFlagReverse = item.featureFlagReverse ?? false
  const isFeatureOk = !featureKey || featureFlagMap[featureKey] === true

  return item.showInMenu === true && isAuthOk && featureFlagReverse != isFeatureOk
}

/**
 * Returns top-level menu items with their visible sub-items nested under `children`.
 * All existing auth and feature flag logic applies to both levels independently.
 *
 * @param {Record<string, AppLinkTypes>} appLink - The map of all possible menu items.
 * @param {boolean} isLoggedIn - Whether the user is authenticated.
 * @param {Record<FeatureFlagKey, boolean>} featureFlagMap - Map of feature flags and their enabled state.
 * @returns {AppLinkWithChildren[]} Array of visible top-level items, each with optional children.
 */
export function getVisibleMenuItems(
  appLink: Record<string, AppLinkTypes>,
  isLoggedIn: boolean,
  featureFlagMap: Record<FeatureFlagKey, boolean>,
): AppLinkWithChildren[] {
  const allItems = Object.entries(appLink) as [AppLinkKey, AppLinkTypes][]

  // Build a lookup keyed by AppLinkKey for parent resolution
  const itemByKey = Object.fromEntries(allItems) as Record<AppLinkKey, AppLinkTypes>

  // Separate top-level and sub-items — top-level have no parent
  const topLevel = allItems
    .filter(([, item]) => !item.parent && isItemVisible(item, isLoggedIn, featureFlagMap))
    .sort(
      ([, a], [, b]) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
    )

  return topLevel.map(([key, item]) => {
    // Find direct children of this top-level item that pass visibility checks
    const children = allItems
      .filter(
        ([, child]) => child.parent === key && isItemVisible(child, isLoggedIn, featureFlagMap),
      )
      .map(([, child]) => child)
    // Sub-items have no order defined — preserve appLink declaration order
    // but you can add order to sub-items in appLink later if needed

    return {
      ...item,
      ...(children.length > 0 ? { children } : {}),
    }
  })
}
